import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Building2, User, CalendarClock } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { Badge, statusVariant } from "../../../components/ui/Badge";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { useMyPayslip } from "../../../hooks/usePayslip";
import { payslipApi } from "../../../lib/payslipApi";
import { formatCurrency, formatPeriod } from "../../../utils/format";

function LineItem({
  label,
  amount,
  bold = false,
  employerAmount,
}: {
  label: string;
  amount: number;
  bold?: boolean;
  employerAmount?: number;
}) {
  return (
    <div className={`py-2 ${bold ? "font-semibold text-[#0f172a]" : "text-[#0f172a]"}`}>
      <div className="flex justify-between">
        <span className={bold ? "font-semibold" : "text-[#64748b]"}>{label}</span>
        <span>{formatCurrency(amount)}</span>
      </div>
      {employerAmount != null && employerAmount > 0 && (
        <div className="flex justify-between mt-0.5 pl-3 text-xs text-[#16a34a]">
          <span>+ Employer contribution</span>
          <span>{formatCurrency(employerAmount)}</span>
        </div>
      )}
    </div>
  );
}

export default function PayslipDetail() {
  const { businessId, period } = useParams<{ businessId: string; period: string }>();
  const navigate = useNavigate();
  const { toasts, toast, dismissToast } = useToast();

  const decodedBusinessId = businessId ? decodeURIComponent(businessId) : "";
  const decodedPeriod = period ? decodeURIComponent(period) : "";
  const { data: payslip, isLoading, error } = useMyPayslip(decodedBusinessId, decodedPeriod);

  const handleDownload = async () => {
    if (!decodedBusinessId || !decodedPeriod) return;
    try {
      const { url } = await payslipApi.downloadMine(decodedBusinessId, decodedPeriod);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !payslip) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <p className="text-[#64748b]">
          Payslip not found for period: {decodedPeriod || "unknown"}
        </p>
        <Button variant="secondary" onClick={() => navigate("/employee/payslip")}>
          <ArrowLeft className="h-4 w-4" />
          Back to Payslips
        </Button>
      </div>
    );
  }

  const totalAllowances = payslip.allowances.reduce((s, a) => s + a.amount, 0);
  const totalIncentives = (payslip.incentives ?? []).reduce((s, a) => s + a.amount, 0);
  const grossPay = payslip.basicPay + totalAllowances + totalIncentives;
  const totalDeductions = payslip.deductions.reduce((s, d) => s + d.amount, 0);
  const totalLoanDeductions = payslip.loanDeductions.reduce((s, d) => s + d.amount, 0);
  const totalCashAdvDeductions = (payslip.cashAdvanceDeductions ?? []).reduce((s, d) => s + d.amount, 0);
  const ppaDeds = (payslip.ppaAdjustments ?? []).filter((a) => a.type === "DEDUCTION");
  const ppaAdds = (payslip.ppaAdjustments ?? []).filter((a) => a.type === "ADDITION");
  const totalPpaDed = ppaDeds.reduce((s, a) => s + a.amount, 0);
  const totalPpaAdd = ppaAdds.reduce((s, a) => s + a.amount, 0);
  const workAdjs = payslip.workAdjustments ?? [];
  const totalWorkAdj = workAdjs.reduce((s, d) => s + d.amount, 0);
  const netPay = payslip.netPay ?? (grossPay - totalDeductions - totalLoanDeductions - totalCashAdvDeductions - totalPpaDed + totalPpaAdd + totalWorkAdj);

  // Map employer contributions by label for O(1) lookup per deduction row
  const employerMap = new Map<string, number>(
    (payslip.employerContributions ?? []).map((c) => [c.label, c.amount]),
  );

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/employee/payslip")}>
          <ArrowLeft className="h-4 w-4" />
          Back to Payslips
        </Button>
      </div>

      {/* Payslip Document */}
      <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-[0_1px_3px_0_rgb(0_0_0/0.07)] overflow-hidden">
        {/* Document Header */}
        <div className="bg-[#2447d9] px-8 py-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 mb-1">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/20">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold">MGC Hub</h2>
                <p className="text-xs text-blue-200">Payroll Department</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-blue-200 uppercase tracking-widest font-semibold">PAYSLIP</p>
              <p className="text-lg font-bold mt-0.5">{formatPeriod(payslip.period)}</p>
              <div className="mt-1">
                <Badge variant={statusVariant(payslip.status)} className="bg-white/20 text-white border-white/30">
                  {payslip.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Employee Info */}
        <div className="px-8 py-5 border-b border-[#e2e8f0] bg-[#f8fafc]">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#dbe4ff]">
              <User className="h-4 w-4 text-[#2447d9]" />
            </div>
            <h3 className="text-sm font-semibold text-[#64748b] uppercase tracking-wide">Employee Information</h3>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div>
              <span className="text-[#94a3b8]">Employee ID</span>
              <p className="font-medium text-[#0f172a]">{payslip.userId}</p>
            </div>
            <div>
              <span className="text-[#94a3b8]">Pay Period</span>
              <p className="font-medium text-[#0f172a]">{formatPeriod(payslip.period)}</p>
            </div>
            <div>
              <span className="text-[#94a3b8]">Status</span>
              <p className="font-medium text-[#0f172a]">{payslip.status}</p>
            </div>
          </div>
        </div>

        {/* Proration notice */}
        {payslip.isProrated && payslip.prorationDetails && (
          <div className="px-8 py-3 border-b border-[#fde68a] bg-[#fffbeb]">
            <div className="flex items-start gap-2">
              <CalendarClock className="h-4 w-4 text-[#d97706] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#92400e]">Prorated Pay Period</p>
                <p className="text-xs text-[#92400e] mt-0.5">
                  Your pay is prorated to your hire date ({payslip.prorationDetails.hireDate}).
                  This payslip covers {payslip.prorationDetails.workedDays} of{" "}
                  {payslip.prorationDetails.totalDays} working days in this period.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Earnings */}
        <div className="px-8 py-5 border-b border-[#e2e8f0]">
          <h3 className="text-sm font-semibold text-[#64748b] uppercase tracking-wide mb-3">Earnings</h3>
          <div className="divide-y divide-[#f1f5f9]">
            <LineItem
              label={payslip.isProrated ? `Basic Pay (${payslip.prorationDetails!.workedDays}/${payslip.prorationDetails!.totalDays} days)` : "Basic Pay"}
              amount={payslip.basicPay}
            />
            {payslip.allowances.map((a) => (
              <LineItem key={a.label} label={a.label} amount={a.amount} />
            ))}
            {(payslip.incentives ?? []).length > 0 && (
              <>
                <p className="pt-3 pb-1 text-xs font-semibold text-[#16a34a] uppercase tracking-wide">Incentives</p>
                {(payslip.incentives ?? []).map((a, i) => (
                  <div key={`inc-${i}`} className="py-2 flex justify-between text-[#16a34a]">
                    <span>{a.label}</span>
                    <span>+ {formatCurrency(a.amount)}</span>
                  </div>
                ))}
              </>
            )}
            {workAdjs.length > 0 && (
              <>
                <p className="pt-3 pb-1 text-xs font-semibold text-[#d97706] uppercase tracking-wide">OT / Holiday Pay</p>
                {workAdjs.map((a, i) => (
                  <div key={`wa-${i}`} className="py-2 flex justify-between text-[#d97706]">
                    <span>{a.label}</span>
                    <span>+ {formatCurrency(a.amount)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
          <div className="mt-2 flex justify-between border-t border-[#e2e8f0] pt-2 font-semibold text-[#0f172a]">
            <span>Gross Pay</span>
            <span className="text-[#2447d9]">{formatCurrency(grossPay)}</span>
          </div>
        </div>

        {/* Deductions */}
        <div className="px-8 py-5 border-b border-[#e2e8f0]">
          <h3 className="text-sm font-semibold text-[#64748b] uppercase tracking-wide mb-1">Deductions</h3>
          {employerMap.size > 0 && (
            <p className="text-xs text-[#16a34a] mb-3">
              Green lines show your employer&apos;s matching contribution — paid by the company, not deducted from your pay.
            </p>
          )}
          <div className="divide-y divide-[#f1f5f9]">
            {payslip.deductions.map((d) => (
              <LineItem
                key={d.label}
                label={d.label}
                amount={d.amount}
                employerAmount={employerMap.get(d.label)}
              />
            ))}
          </div>
          {payslip.loanDeductions.length > 0 && (
            <>
              <p className="mt-3 mb-1 text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Loan Deductions</p>
              <div className="divide-y divide-[#f1f5f9]">
                {payslip.loanDeductions.map((d) => (
                  <LineItem key={d.label} label={d.label} amount={d.amount} />
                ))}
              </div>
            </>
          )}
          {(payslip.cashAdvanceDeductions ?? []).length > 0 && (
            <>
              <p className="mt-3 mb-1 text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Cash Advance</p>
              <div className="divide-y divide-[#f1f5f9]">
                {(payslip.cashAdvanceDeductions ?? []).map((d, i) => (
                  <LineItem key={`ca-${i}`} label={d.label} amount={d.amount} />
                ))}
              </div>
            </>
          )}
          {ppaDeds.length > 0 && (
            <>
              <p className="mt-3 mb-1 text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Other Deductions</p>
              <div className="divide-y divide-[#f1f5f9]">
                {ppaDeds.map((d, i) => (
                  <LineItem key={`ppd-${i}`} label={d.label} amount={d.amount} />
                ))}
              </div>
            </>
          )}
          {ppaAdds.length > 0 && (
            <>
              <p className="mt-3 mb-1 text-xs font-semibold text-[#16a34a] uppercase tracking-wide">Additions</p>
              <div className="divide-y divide-[#f1f5f9]">
                {ppaAdds.map((a, i) => (
                  <div key={`ppa-${i}`} className="py-2 flex justify-between text-[#16a34a]">
                    <span>{a.label}</span>
                    <span>+ {formatCurrency(a.amount)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          <div className="mt-2 flex justify-between border-t border-[#e2e8f0] pt-2 font-semibold text-[#dc2626]">
            <span>Total Deductions</span>
            <span>- {formatCurrency(totalDeductions + totalLoanDeductions + totalCashAdvDeductions + totalPpaDed)}</span>
          </div>
        </div>

        {/* Net Pay */}
        <div className="px-8 py-6 bg-[#eff3ff]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-[#64748b] uppercase tracking-wide">Net Pay</p>
              <p className="text-xs text-[#94a3b8] mt-0.5">Amount credited to your account</p>
            </div>
            <p className="text-3xl font-extrabold text-[#2447d9]">{formatCurrency(netPay)}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button variant="secondary" onClick={() => navigate("/employee/payslip")}>
          <ArrowLeft className="h-4 w-4" />
          Back to Payslips
        </Button>
        {payslip.status === "FINALIZED" && (
          <Button variant="ghost" onClick={handleDownload}>
            <Download className="h-4 w-4" />
            Download PDF
          </Button>
        )}
      </div>
    </div>
  );
}
